import { Link } from "react-router-dom";
import { ChevronLeft, Shield, Lock, FileText, UserCheck, Mail } from "lucide-react";
import PublicNav from "@/components/PublicNav";
import { useLanguage } from "@/context/LanguageContext";

const COPY = {
  tr: {
    home: "Ana Sayfa",
    lastUpdated: "Son güncelleme",
    lastUpdatedValue: "Şubat 2026",
    title: "Gizlilik Politikası",
    intro:
      "KAHU, sokak canlarına yuva bulmak ve şeffaf bakım sağlamak için kurulmuş bir dijital platformdur. Sizden topladığımız her bilgiye, kendi verimiz gibi özen gösteriyoruz. Bu sayfada hangi verileri neden topladığımızı, nasıl kullandığımızı ve haklarınızı sade bir dille açıklıyoruz.",
    s1Title: "1. Hangi Verileri Topluyoruz?",
    s1Lead: "Yalnızca ihtiyaç duyduğumuz minimum veriyi alıyoruz:",
    s1Items: [
      ["Bağış / Destek formlarında:", "Ad soyad, e-posta, telefon, bağış tutarı."],
      ["Sahiplenme başvurularında:", "Ad soyad, iletişim, adres ve yaşam ortamına dair gönüllü olarak paylaştığınız bilgiler."],
      ["Koruyucu Aile başvurularında:", "Ad soyad, iletişim, aylık destek tutarı."],
      ["İletişim formunda:", "Ad soyad, e-posta ve mesaj içeriğiniz."],
      ["Otomatik olarak:", "Anonim site kullanım istatistikleri (ziyaret sayısı, sayfa görüntülemeleri). Kimlik bilgisi içermez."],
    ],
    s2Title: "2. Bu Verileri Neden İstiyoruz?",
    s2Items: [
      ["Bağışlarınız için", "makbuz düzenleyebilmek", "."],
      ["Sahiplenme ve Koruyucu Aile başvurularını", "değerlendirip size geri dönmek", "."],
      ["Sahiplendiğiniz can için", "aylık güncelleme", "ve makbuz e-postaları iletmek."],
      ["Hesaplarımıza yatan bağışları doğru kişiye", "eşleştirmek", "."],
      ["Mesajlarınıza ve sorularınıza dönüş yapmak.", "", ""],
    ],
    s3Title: "3. Verileriniz Nasıl Korunuyor?",
    s3Items: [
      "Tüm veriler HTTPS şifreli bağlantı üzerinden iletilir.",
      "Veritabanımız erişimi sadece yetkili ekip üyelerine açıktır.",
      "Hassas işlemler (sahiplenme onayı, bağış doğrulama) günlük olarak kayıt altına alınır.",
      "Kullanılmadığı belirlenen veriler, makul süre içinde silinir veya anonimleştirilir.",
    ],
    s4Title: "4. Üçüncü Taraflar",
    s4Lead: "Hizmetin işleyişi için sınırlı sayıda dış sağlayıcıdan yararlanırız. Bu sağlayıcılar yalnızca işlem yapmak için gereken veriye erişebilir ve KVKK uyumludur:",
    s4Items: [
      ["Ödeme", "banka havalesi (IBAN) ve onaylı ödeme altyapısı."],
      ["E-posta gönderimi", "Google Workspace ve Resend (yedek)."],
      ["Veri barındırma", "Avrupa konumlu güvenli sunucular."],
    ],
    s5Title: "5. Haklarınız (",
    s5TitleTail: ")",
    s5Lead: "6698 sayılı Kişisel Verilerin Korunması Kanunu kapsamında her zaman:",
    s5Items: ["Hangi verilerinizin işlendiğini öğrenme,", "Yanlışları düzeltme,", "Verilerinizin silinmesini isteme,", "Verilerinizin işlenmesine itiraz etme hakkına sahipsiniz."],
    s5Note: "Bu haklarınızı kullanmak için aşağıdaki iletişim kanalından bize yazmanız yeterlidir.",
    s6Title: "6. Bize Ulaşın",
    s6Body: "Gizlilikle ilgili her türlü sorunuz, talebiniz veya KVKK kapsamındaki haklarınızı kullanmak için bizimle iletişime geçebilirsiniz.",
    s6Cta: "İletişim Sayfasına Git",
    footer: "Bu politika, hizmetlerimiz veya yasal gerekliliklerdeki değişikliklere göre güncellenebilir. Önemli değişiklikler bu sayfada duyurulur.",
  },
  en: {
    home: "Home",
    lastUpdated: "Last updated",
    lastUpdatedValue: "February 2026",
    title: "Privacy Policy",
    intro:
      "KAHU is a digital platform founded to help stray companions find homes and to provide transparent care. We treat every piece of information you share with the same care we give our own data. On this page we explain in plain language what we collect, why, how we use it and your rights.",
    s1Title: "1. What Data Do We Collect?",
    s1Lead: "We collect only the minimum data we need:",
    s1Items: [
      ["On donation / support forms:", "Full name, email, phone, donation amount."],
      ["On adoption applications:", "Full name, contact information, address and details about your living environment that you voluntarily share."],
      ["On Guardian Family applications:", "Full name, contact information, monthly support amount."],
      ["On the contact form:", "Full name, email and the content of your message."],
      ["Automatically:", "Anonymous site usage statistics (visit count, page views). Does not include personal identity information."],
    ],
    s2Title: "2. Why Do We Ask for This Data?",
    s2Items: [
      ["So we can", "issue receipts", "for your donations."],
      ["To", "evaluate and respond", "to Adoption and Guardian Family applications."],
      ["To share", "monthly updates", "and receipt emails for the companion you adopted."],
      ["To", "match", "donations received in our accounts to the right person."],
      ["To reply to your messages and questions.", "", ""],
    ],
    s3Title: "3. How Is Your Data Protected?",
    s3Items: [
      "All data is transmitted over an HTTPS-encrypted connection.",
      "Database access is limited to authorised team members only.",
      "Sensitive operations (adoption approval, donation verification) are logged daily.",
      "Data that is no longer used is deleted or anonymised within a reasonable timeframe.",
    ],
    s4Title: "4. Third Parties",
    s4Lead: "For the service to operate we rely on a limited number of external providers. These providers can only access the data required to perform the operation and are KVKK-compliant:",
    s4Items: [
      ["Payment", "bank transfer (IBAN) and approved payment infrastructure."],
      ["Email delivery", "Google Workspace and Resend (backup)."],
      ["Data hosting", "secure servers located in Europe."],
    ],
    s5Title: "5. Your Rights (",
    s5TitleTail: ")",
    s5Lead: "Under Turkey's KVKK (Law No. 6698 on the Protection of Personal Data), you always have the right to:",
    s5Items: [
      "learn which of your data is being processed,",
      "correct errors,",
      "request deletion of your data,",
      "object to the processing of your data.",
    ],
    s5Note: "To exercise these rights, simply write to us through the contact channel below.",
    s6Title: "6. Reach Out to Us",
    s6Body: "You can contact us with any privacy question, request, or to exercise your rights under KVKK.",
    s6Cta: "Go to Contact Page",
    footer: "This policy may be updated as our services or legal requirements evolve. Material changes will be announced on this page.",
  },
};

export default function PrivacyPage() {
  const { locale } = useLanguage();
  const c = COPY[locale] || COPY.tr;

  return (
    <div className="min-h-screen bg-background">
      <PublicNav />

      <section className="py-12 md:py-20">
        <div className="max-w-3xl mx-auto px-4 md:px-8">
          <Link to="/" className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground mb-6 transition-colors" data-testid="privacy-back">
            <ChevronLeft className="w-4 h-4" />
            {c.home}
          </Link>

          <div className="mb-10">
            <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-primary/10 mb-4">
              <Shield className="w-7 h-7 text-primary" strokeWidth={1.8} />
            </div>
            <h1 className="font-heading text-4xl md:text-5xl tracking-tight mb-3">{c.title}</h1>
            <p className="text-muted-foreground text-base">{c.lastUpdated}: {c.lastUpdatedValue}</p>
          </div>

          <div className="prose prose-base max-w-none text-foreground/90 leading-relaxed space-y-8">
            <p className="text-lg text-muted-foreground">{c.intro}</p>

            <div>
              <div className="flex items-center gap-3 mb-3">
                <FileText className="w-5 h-5 text-primary" />
                <h2 className="font-heading text-2xl m-0">{c.s1Title}</h2>
              </div>
              <p>{c.s1Lead}</p>
              <ul className="list-disc pl-6 space-y-1.5 mt-2">
                {c.s1Items.map(([bold, rest], i) => (
                  <li key={i}><strong>{bold}</strong> {rest}</li>
                ))}
              </ul>
            </div>

            <div>
              <div className="flex items-center gap-3 mb-3">
                <UserCheck className="w-5 h-5 text-primary" />
                <h2 className="font-heading text-2xl m-0">{c.s2Title}</h2>
              </div>
              <ul className="list-disc pl-6 space-y-1.5 mt-2">
                {c.s2Items.map(([pre, mid, post], i) => (
                  <li key={i}>{pre} {mid && <strong>{mid}</strong>} {post}</li>
                ))}
              </ul>
            </div>

            <div>
              <div className="flex items-center gap-3 mb-3">
                <Lock className="w-5 h-5 text-primary" />
                <h2 className="font-heading text-2xl m-0">{c.s3Title}</h2>
              </div>
              <ul className="list-disc pl-6 space-y-1.5 mt-2">
                {c.s3Items.map((item, i) => <li key={i}>{item}</li>)}
              </ul>
            </div>

            <div>
              <div className="flex items-center gap-3 mb-3">
                <Shield className="w-5 h-5 text-primary" />
                <h2 className="font-heading text-2xl m-0">{c.s4Title}</h2>
              </div>
              <p>{c.s4Lead}</p>
              <ul className="list-disc pl-6 space-y-1.5 mt-2">
                {c.s4Items.map(([bold, rest], i) => (
                  <li key={i}><strong>{bold}</strong> — {rest}</li>
                ))}
              </ul>
            </div>

            <div>
              <h2 className="font-heading text-2xl">
                {c.s5Title}
                <Link to="/kvkk" className="text-primary hover:underline">KVKK</Link>
                {c.s5TitleTail}
              </h2>
              <p>{c.s5Lead}</p>
              <ul className="list-disc pl-6 space-y-1.5 mt-2">
                {c.s5Items.map((item, i) => <li key={i}>{item}</li>)}
              </ul>
              <p className="mt-3">{c.s5Note}</p>
            </div>

            <div className="bg-primary/5 border border-primary/20 rounded-2xl p-6 md:p-8 not-prose">
              <div className="flex items-center gap-3 mb-3">
                <Mail className="w-5 h-5 text-primary" />
                <h2 className="font-heading text-2xl m-0">{c.s6Title}</h2>
              </div>
              <p className="text-foreground/90 mb-4">{c.s6Body}</p>
              <Link to="/iletisim" data-testid="privacy-contact-link">
                <span className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-primary text-primary-foreground font-semibold transition-all duration-200 hover:bg-primary hover:text-white hover:-translate-y-0.5 hover:shadow-lg hover:shadow-primary/30">
                  <Mail className="w-4 h-4" />
                  {c.s6Cta}
                </span>
              </Link>
            </div>

            <p className="text-sm text-muted-foreground pt-4 border-t border-border/40 mt-8">{c.footer}</p>
          </div>
        </div>
      </section>
    </div>
  );
}
